// =============================================================================
// CONFIGURATION GRAPHIQUES B8ILD - STYLE PREMIUM
// =============================================================================

import { SEUILS_MARGE, getSeuilsParMetier } from '@/lib/calculs-rentabilite';

// Réexport pour faciliter l'utilisation
export { SEUILS_MARGE, getSeuilsParMetier };

// =============================================================================
// PALETTE COULEURS PREMIUM
// =============================================================================

/**
 * Couleurs vibrantes pour les séries de données
 * Inspirées du screenshot avec courbes colorées fluides
 */
export const CHART_SERIES_COLORS = {
  serie1: '#FF6B9D', // Rose corail
  serie2: '#FFB84D', // Orange doré (proche accent B8ild)
  serie3: '#4ECDC4', // Turquoise
  serie4: '#95E87A', // Vert lime
  serie5: '#A78BFA', // Violet clair
} as const;

/**
 * Couleurs B8ild pour statuts et seuils
 */
export const CHART_STATUS_COLORS = {
  // Rentabilité
  excellent: '#10B981', // Vert emerald >= 22%
  bon: '#34D399',       // Vert clair >= 18%
  attention: '#FBBF24', // Jaune >= 12%
  fragile: '#F59E0B',   // Orange >= 5%
  critique: '#F97316',  // Orange foncé >= 0%
  deficit: '#EF4444',   // Rouge < 0%

  // Catégories de coûts principales
  mainOeuvre: '#3B82F6',     // Bleu
  materiaux: '#FFB84D',      // Orange doré
  sousTraitance: '#A78BFA',  // Violet
  frais: '#6B7280',          // Gris

  // Couleurs détaillées par type de frais
  locationVehicule: '#F97316',    // Orange
  achatOutillage: '#EAB308',      // Jaune
  locationOutillage: '#84CC16',   // Vert lime
  consommables: '#22C55E',        // Vert
  deplacement: '#14B8A6',         // Teal
  hebergement: '#06B6D4',         // Cyan
  assurance: '#0EA5E9',           // Sky
  permis: '#8B5CF6',              // Violet
  sousTraitant: '#A78BFA',        // Violet clair
  penaliteRetard: '#EF4444',      // Rouge
  autre: '#6B7280',               // Gris
  fournisseurs: '#F59E0B',        // Amber
} as const;

// Mapping des types de frais vers les couleurs
export const FRAIS_TYPE_COLORS: Record<string, string> = {
  'Location de véhicule': CHART_STATUS_COLORS.locationVehicule,
  'Achat d\'outillage': CHART_STATUS_COLORS.achatOutillage,
  'Location d\'outillage': CHART_STATUS_COLORS.locationOutillage,
  'Consommables': CHART_STATUS_COLORS.consommables,
  'Frais de déplacement': CHART_STATUS_COLORS.deplacement,
  'Frais d\'hébergement': CHART_STATUS_COLORS.hebergement,
  'Assurance': CHART_STATUS_COLORS.assurance,
  'Permis & autorisations': CHART_STATUS_COLORS.permis,
  'Sous-traitant': CHART_STATUS_COLORS.sousTraitant,
  'Pénalité de retard': CHART_STATUS_COLORS.penaliteRetard,
  'Autre': CHART_STATUS_COLORS.autre,
};

/**
 * Configuration des dégradés SVG pour les graphiques
 */
export const CHART_GRADIENTS = {
  // Dégradés pour les aires sous les courbes
  area1: { start: '#FF6B9D', end: '#FF6B9D00' },
  area2: { start: '#FFB84D', end: '#FFB84D00' },
  area3: { start: '#4ECDC4', end: '#4ECDC400' },
  area4: { start: '#95E87A', end: '#95E87A00' },

  // Dégradé de fond
  background: {
    start: 'hsl(0 0% 15%)',
    end: 'hsl(0 0% 10%)',
  },
} as const;

// =============================================================================
// FONCTIONS UTILITAIRES
// =============================================================================

/**
 * Retourne la couleur selon le taux de marge (seuils BTP génériques)
 */
export function getMargeColor(marge: number): string {
  if (marge >= SEUILS_MARGE.EXCELLENT) return CHART_STATUS_COLORS.excellent;
  if (marge >= SEUILS_MARGE.BON) return CHART_STATUS_COLORS.bon;
  if (marge >= SEUILS_MARGE.CORRECT) return CHART_STATUS_COLORS.attention;
  if (marge >= SEUILS_MARGE.FRAGILE) return CHART_STATUS_COLORS.fragile;
  if (marge >= SEUILS_MARGE.CRITIQUE) return CHART_STATUS_COLORS.critique;
  return CHART_STATUS_COLORS.deficit;
}

/**
 * Retourne la couleur selon le taux de marge et le métier de l'entreprise
 * Utilise les seuils spécifiques au métier (Cerfrance) ou génériques (BTP)
 *
 * @param marge - Taux de marge en pourcentage
 * @param metierKey - Clé du métier (optionnel)
 */
export function getMargeColorParMetier(marge: number, metierKey?: string): string {
  const seuils = getSeuilsParMetier(metierKey);

  // 7 niveaux de marge (même couleur pour exceptionnel et excellent)
  if (marge >= seuils.exceptionnel) return CHART_STATUS_COLORS.excellent;
  if (marge >= seuils.excellent) return CHART_STATUS_COLORS.excellent;
  if (marge >= seuils.bon) return CHART_STATUS_COLORS.bon;
  if (marge >= seuils.correct) return CHART_STATUS_COLORS.attention;
  if (marge >= seuils.fragile) return CHART_STATUS_COLORS.fragile;
  if (marge >= seuils.critique) return CHART_STATUS_COLORS.critique;
  return CHART_STATUS_COLORS.deficit;
}

/**
 * Retourne la couleur selon le % de budget consommé
 */
export function getBudgetColor(pctConsomme: number): string {
  if (pctConsomme < 70) return CHART_STATUS_COLORS.excellent;
  if (pctConsomme < 90) return CHART_STATUS_COLORS.attention;
  return CHART_STATUS_COLORS.deficit;
}

/**
 * Formate un montant pour l'affichage dans les graphiques (compact)
 * Arrondi à l'euro près pour plus de lisibilité
 */
export function formatMontantChart(montant: number): string {
  if (Math.abs(montant) >= 1000000) {
    return (montant / 1000000).toFixed(1).replace('.', ',') + ' M€';
  }
  if (Math.abs(montant) >= 1000) {
    return (montant / 1000).toFixed(0) + ' k€';
  }
  // Arrondir à l'euro pour les petits montants
  return Math.round(montant).toLocaleString('fr-FR') + ' €';
}

/**
 * Formate un montant arrondi à la dizaine (pour Budget)
 * Ex: 4 973 € → 4 970 €, 5 128 € → 5 130 €
 */
export function formatMontantDizaine(montant: number): string {
  // Arrondir à la dizaine la plus proche
  const arrondi = Math.round(montant / 10) * 10;
  return arrondi.toLocaleString('fr-FR') + ' €';
}

/**
 * Formate un pourcentage
 */
export function formatPourcentage(value: number): string {
  return value.toFixed(1).replace('.', ',') + ' %';
}

/**
 * Tronque un texte avec ellipsis
 */
export function truncateText(text: string, maxLength: number = 15): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

// =============================================================================
// TOOLTIPS EXPLICATIFS
// =============================================================================

/**
 * Textes explicatifs pour les tooltips
 */
export const TOOLTIP_EXPLANATIONS = {
  marge: 'La marge représente votre bénéfice après déduction de tous les coûts (main d\'œuvre, matériaux, sous-traitance, frais).',
  rentabilite: 'Le taux de rentabilité compare votre marge au budget initial. Objectif BTP : 18% minimum.',
  budget: 'Progression de vos dépenses par rapport au budget total alloué au chantier.',
  mainOeuvre: 'Coût total de la main d\'œuvre : salaires, charges sociales et patronales de vos équipes.',
  materiaux: 'Factures fournisseurs pour les matériaux de construction.',
  sousTraitance: 'Montants versés aux sous-traitants et prestataires externes.',
  frais: 'Frais annexes : déplacements, location matériel, assurances, etc.',
  coutsCumules: 'Total des dépenses engagées depuis le début du chantier.',
} as const;

// =============================================================================
// DIMENSIONS ET STYLES
// =============================================================================

export const CHART_DIMENSIONS = {
  containerHeight: 380,
  margin: { top: 20, right: 30, bottom: 60, left: 60 },
} as const;

/**
 * Style commun pour les tooltips premium
 */
export const tooltipContainerStyle = {
  backgroundColor: 'hsl(0 0% 18% / 0.95)',
  backdropFilter: 'blur(8px)',
  border: '1px solid hsl(0 0% 30%)',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  pointerEvents: 'none' as const,
} as const;

/**
 * Style pour les légendes
 */
export const legendStyle = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: '16px',
  padding: '12px 0',
  fontSize: '13px',
};

// =============================================================================
// ANIMATIONS
// =============================================================================

export const CHART_ANIMATIONS = {
  // Durée d'animation pour l'apparition des courbes
  duration: 1500,
  // Easing pour un effet fluide
  easing: 'ease-out',
} as const;
